import React, { FC, useCallback, useMemo, useState } from 'react';
import { debounce, partition } from 'lodash';
import latinize from 'latinize';
import { MultiValue } from 'react-select';
import { ItemSelectionDropdown } from '@src/components/ItemSelectionDropdown';
import DescriptionField from '@src/components/DescriptionField';
import { trackUpdateCompanyProfile } from '@src/metrics';
import { AreaType, CountryType, PlaceTypeEnum, useGetPlacesQuery } from '@src/graphql/generated';
import { Option } from '@src/components/Select/interfaces/option.interface';
import { MarketsData } from './interfaces/marketsData.interface';
import { MarketsProps } from './props';
import './styles.scss';

export const Markets: FC<MarketsProps> = ({ data, onChange }) => {
  const [searchPattern, setSearchPattern] = useState('');

  const setDefaultValues = (
    data?: MarketsData,
  ): (Pick<AreaType, 'id' | 'longName'> | Pick<CountryType, 'id' | 'longName' | 'shortName'>)[] => {
    if (data?.isGlobal) {
      return [];
    }

    return [...(data?.marketAreas || []), ...(data?.marketCountries || [])];
  };

  const [items, setItems] = useState(setDefaultValues(data));

  const selectedOptions = useMemo(() => {
    return items.map(item => {
      return {
        label: item.longName,
        value: item.id,
      };
    });
  }, [items]);

  const {
    data: placesData,
    loading: isPlacesLoading,
    refetch: refetchPlaces,
  } = useGetPlacesQuery({
    variables: {
      paginationOptions: {
        limit: 10,
      },
      placeTypes: [PlaceTypeEnum.AREA, PlaceTypeEnum.COUNTRY],
    },
  });

  const places = placesData?.places.result || [];

  const options = useMemo(() => {
    if (isPlacesLoading) {
      return [];
    }

    return (
      places
        .map(item => {
          return {
            label: item.entity?.longName,
            value: item.entity?.id,
          };
        })
        .filter((option): option is Option => {
          return !!option.label && !!option.value;
        }) || []
    );
  }, [isPlacesLoading, places]);

  const handleSearch = useCallback(
    debounce((value: string) => {
      refetchPlaces({
        searchPattern: encodeURIComponent(latinize(value)),
      });
    }, 300),
    [],
  );

  const handleInputChange = (value: string): void => {
    setSearchPattern(value);

    const trimmedValue = value.trim();

    if (!trimmedValue.length) {
      return;
    }

    handleSearch(trimmedValue);
  };

  const onSelect = (selectedOptions: MultiValue<Option>) => {
    const selectedPlaces = places
      .filter(place => {
        return (
          !!place.entity?.id &&
          selectedOptions.some(item => {
            return item.value === place.entity?.id;
          })
        );
      })
      .map(place => place.entity) as (
      | Pick<AreaType, 'id' | 'longName' | '__typename'>
      | Pick<CountryType, 'id' | 'longName' | 'shortName' | '__typename'>
    )[];

    setItems(selectedPlaces);

    const marketAreas = data?.marketAreas || [];
    const marketCountries = data?.marketCountries || [];

    const [countries, areas] = partition(selectedPlaces, item => {
      return item?.__typename === 'CountryType';
    });

    if (marketAreas?.length !== areas.length) {
      onChange('marketAreas', areas);

      trackUpdateCompanyProfile({
        'Market Areas': areas.map(area => area.longName),
      });
    }

    if (marketCountries?.length !== countries.length) {
      onChange('marketCountries', countries);

      trackUpdateCompanyProfile({
        'Market Countries': countries.map(country => country.longName),
      });
    }
  };

  return (
    <div className="company-markets">
      <DescriptionField
        className="company-markets__description"
        defaultValue={data?.marketsDescription}
        maxLength={750}
        onBlur={e =>
          trackUpdateCompanyProfile({
            'Markets Description': e.target.value,
          })
        }
        onChange={value => onChange('marketsDescription', value)}
        placeholder="Type here..."
        title="About our markets"
        titleSize="XS"
      />

      <ItemSelectionDropdown
        options={options}
        onSelect={onSelect}
        onInputChange={handleInputChange}
        inputValue={searchPattern}
        placeholder="Search for a country or region"
        defaultValue={selectedOptions}
      />
    </div>
  );
};
